import { ApiV1_Product } from '@pedix-workspace/api-v1';
import {
  CartItem,
  CartItemOptionItems,
  CartItemProduct,
  OptionConfiguration,
  PricingError,
  Product,
  ProductOptionItem,
} from '@pedix-workspace/utils';

export type PricingItemType = 'product' | 'presentation' | 'option-item';

export type PricingItem = {
  type: PricingItemType;
  id: string;
  name: string;
  price: number;
  priceDiscount?: number;
  clonedFrom: string | undefined;
  parentItem?: {
    id: string;
    name: string;
    clonedFrom: string | undefined;
  };
};

function generatePricingItem(
  type: PricingItemType,
  item: CartItemProduct | ProductOptionItem,
  parent?: CartItemProduct | OptionConfiguration,
): PricingItem {
  return {
    type,
    id: item.id,
    name: item.name,
    price: item.price,
    priceDiscount: item.priceDiscount,
    clonedFrom: item.clonedFrom,
    parentItem: parent
      ? {
          id: parent.id,
          name: parent.name,
          clonedFrom: parent.clonedFrom,
        }
      : undefined,
  };
}

export function cartItemsToPricingItems(
  cartItems: Pick<CartItem, 'product' | 'selectedPresentation' | 'selectedOptions'>[],
): Record<string, PricingItem> {
  return cartItems.reduce((pricingItemsMap, cartItem) => {
    if (cartItem.selectedPresentation) {
      cartItem.selectedPresentation.items.forEach(presentationItem => {
        pricingItemsMap[`presentation-${presentationItem.id}`] = generatePricingItem(
          'presentation',
          presentationItem,
          cartItem.product,
        );
      });
    } else {
      pricingItemsMap[`product-${cartItem.product.id}`] = generatePricingItem(
        'product',
        cartItem.product,
      );
    }

    if (cartItem.selectedOptions) {
      cartItem.selectedOptions.forEach(selectedOption => {
        selectedOption.items.forEach(optionItem => {
          pricingItemsMap[`option-item-${optionItem.id}`] = generatePricingItem(
            'option-item',
            optionItem,
            selectedOption.option,
          );
        });
      });
    }

    return pricingItemsMap;
  }, {});
}

export function productPriceChanged(
  productA: Pick<Product, 'price' | 'priceDiscount'>,
  productB: Pick<Product, 'price' | 'priceDiscount'>,
): boolean {
  const priceA = productA.priceDiscount || productA.price;
  const priceB = productB.priceDiscount || productB.price;

  return priceA !== priceB;
}

export function generatePricingError(
  type: PricingItemType,
  item: Product | ProductOptionItem,
  oldPricing: Pick<Product, 'price' | 'priceDiscount'>,
  parentItem?: Pick<Product, 'id' | 'name' | 'clonedFrom'>,
): PricingError {
  return {
    type,
    id: item.id,
    name: item.name,
    oldPrice: oldPricing.price,
    oldPriceDiscount: oldPricing.priceDiscount,
    newPrice: item.price,
    newPriceDiscount: item.priceDiscount,
    clonedFrom: item.clonedFrom,
    parentItem: parentItem
      ? {
          id: parentItem.id,
          name: parentItem.name,
          clonedFrom: parentItem.clonedFrom,
        }
      : undefined,
  };
}

export function validatePricingChanges(
  productsInDb: Product[] | ApiV1_Product[],
  cartItems: Pick<CartItem, 'product' | 'selectedPresentation' | 'selectedOptions'>[],
): PricingError[] {
  const pricingItems = cartItemsToPricingItems(cartItems);

  return Object.values(pricingItems).reduce(
    (pricingErrors, pricingItem) => {
      switch (pricingItem.type) {
        case 'product':
        case 'option-item': {
          const sourceProductId = pricingItem.clonedFrom || pricingItem.id;

          const matchedProduct = productsInDb.find(product => {
            return sourceProductId === (product.clonedFrom || product.id);
          });
          if (matchedProduct) {
            if (productPriceChanged(matchedProduct, pricingItem)) {
              pricingErrors.push(
                generatePricingError(pricingItem.type, matchedProduct, pricingItem),
              );
            }
          }
          break;
        }
        case 'presentation': {
          const sourceProductId = pricingItem.parentItem.clonedFrom || pricingItem.parentItem.id;
          const sourcePresentationItemId = pricingItem.clonedFrom || pricingItem.id;

          const matchedProduct = productsInDb.find(product => {
            return sourceProductId === (product.clonedFrom || product.id);
          });

          if (matchedProduct && matchedProduct.presentations) {
            const matchedPresentationItem = matchedProduct.presentations.items.find(currentItem => {
              return sourcePresentationItemId === (currentItem.clonedFrom || currentItem.id);
            });
            if (matchedPresentationItem) {
              if (productPriceChanged(matchedPresentationItem, pricingItem)) {
                pricingItem.parentItem = {
                  id: matchedProduct.id,
                  name: matchedProduct.name,
                  clonedFrom: matchedProduct.clonedFrom,
                };
                pricingErrors.push(
                  generatePricingError(
                    'presentation',
                    matchedPresentationItem,
                    pricingItem,
                    matchedProduct,
                  ),
                );
              }
            }
          }
          break;
        }
      }
      return pricingErrors;
    },
    <PricingError[]>[],
  );
}
